{
  "en": {
    "profile": {
      "subNav": {
        "general": "My Profile",
        "account": "Edit Account",
        "privacy": "Privacy",
        "licenses": "My Licenses",
        "emailSettings": "Email Settings",
        "authorizedApplications": "Partner Logins",
        "export": "Data Export"
      },
      "general": {
        "title": "Profile",
        "sectionName": "Name",
        "sectionLocality": "Profession & School",
        "sectionCommunity": "Community",
        "sectionSettings": "Settings",
        "nameHint": "We need this information for the certificate of attendance.",
        "nameHintInternational": "Your full name will only be used when you participate in one of our further educational programs in order to provide you with an official certificate of attendance.",
        "localityHint": "Improve your personal recommendations by adding more information (optional).",
        "localityHintInternational": "With the following information you can help us to create the best experience for you:",
        "communityHint": "These details are optional and will be used in our gallery for educational resources.",
        "firstname": "First name",
        "lastname": "Last name",
        "job": "Profession",
        "customJob": "Other profession",
        "schoolType": "School type",
        "schoolTypeCustom": "Other school type",
        "country": "Country",
        "region": "State",
        "regionCanton": "Canton",
        "regionNoCountry": "Select a country first.",
        "fromSchool": "Comes from your school.",
        "gradeList": "Grade Levels",
        "subjectList": "Subjects",
        "customSubject": "Other subject",
        "avatar": "Profile image",
        "avatarHelp": "200x200 pixel recommended",
        "avatarUploadError": "The profile image could not be uploaded.",
        "avatarUploadProgress": "Profile picture is being uploaded",
        "communityName": "Community name",
        "communityNameHelp": "If you want to use a different name for sharing materials, feel free to enter it here. Otherwise we will just use your first and last name.",
        "timezone": "Timezone",
        "timeFormat": "Time format",
        "locale": "User interface language",
        "contentLanguage": "Content language",
        "contentLanguageOptions": {
          "false": "German-language programs 🇩🇪 🇦🇹 🇨🇭",
          "true": "English-language programs 🌎"
        },
        "timeFormatOptions": {
          "TWELVE_HOURS": "12 hours",
          "TWENTY_FOUR_HOURS": "24 hours"
        },
        "jobOptions": {
          "JOB_TEACHER": "Teacher",
          "JOB_LEADING_TEACHER": "Leading teacher",
          "JOB_PRINCIPAL": "Principal",
          "JOB_UNIVERSITY_STUDENT": "University student",
          "JOB_TEACHER_TRAINEE": "Teacher trainee",
          "JOB_PARENT": "Parent",
          "JOB_SOCIAL_WORKER": "Social worker",
          "JOB_FACULTY_MEMBER": "Faculty member",
          "JOB_ADMINISTRATIVE_STAFF": "Administrative staff",
          "JOB_OTHER": "Other"
        },
        "selectPlaceholder": "Please select",
        "privacyDeletionNotice": "Privacy policy notice: If your account remains inactive for over two years, we will notify you via email that it is scheduled for automatic deletion. If you do not reactivate it within one month after the notification, it will be deleted automatically.",
        "save": "Save",
        "cancel": "Cancel",
        "deleteAccount": "Delete Account",
        "deleteAccountDescription": "Deleting your account is irreversible. All your data will be permanently removed.",
        "deleteAccountConfirmation": "Are you sure you want to delete your account?",
        "deleteAccountButton": "Delete account permanently",
        "successMessage": "Profile updated successfully.",
        "errorMessage": "The profile could not be updated."
      },
      "account": {
        "title": "Edit Account",
        "email": "Email",
        "password": "New password",
        "passwordHelp": "Leave blank if you don't want to change it.",
        "currentPassword": "Current password",
        "currentPasswordHelp": "We need your current password to confirm changes.",
        "save": "Save",
        "cancel": "Cancel",
        "invoiceEmailNotice": "To change your billing email, please visit the <a href=\"{portalUrl}\">subscription management</a>.",
        "unconfirmedEmail": "We sent a confirmation email to your new address. Please confirm it.",
        "oauthConnected": "Connected with",
        "oauthConnect": "Connect your fobizz account",
        "connectButton": "Connect with {provider}",
        "disconnect": "Disconnect",
        "fobizzId": "fobizz ID",
        "successMessage": "Account updated successfully.",
        "errorMessage": "The account could not be updated."
      },
      "disconnectOauth": {
        "title": "Disconnect from {provider}",
        "hint": "Please set a password to be able to log in after the disconnect.",
        "password": "New password",
        "submit": "Disconnect",
        "cancel": "Cancel",
        "successMessage": "The connection was successfully disconnected.",
        "errorMessage": "The connection could not be disconnected."
      },
      "privacy": {
        "title": "Privacy Settings",
        "embedConsent": {
          "paragraph": "You agree that video content will be loaded into our professional development courses and that the necessary data, in particular your IP address, will be transmitted to the content provider for this purpose. With this consent, it is possible to watch the videos in our courses. Further information can be found in our privacy policy.",
          "agree": "Give consent",
          "disagree": "Revoke consent"
        }
      },
      "licenses": {
        "title": "My Licenses",
        "sectionSubscriptions": "Subscriptions and Individual Licenses",
        "subtitleSubscriptions": "You have booked the following individual licenses:",
        "sectionMemberships": "License Group Memberships",
        "subtitleMembershipsWithGroups": "The following license groups entitle you to use the various offerings:",
        "manageSubscription": "Manage Subscription",
        "portalError": "The subscription portal could not be opened right now.",
        "buyNow": "Buy now",
        "activatedAt": "Active since",
        "nextBilling": "Upcoming payment",
        "billingPeriod": "Payment interval",
        "billingPeriodUnits": {
          "year": "Yearly",
          "month": "Monthly"
        },
        "paused": "Paused",
        "resumed": "Resumed",
        "cancelledAt": "Cancelled on",
        "usageUntil": "Can be used until",
        "price": "Price",
        "statusExpired": "Expired",
        "statuses": {
          "ACTIVE": "Active",
          "IN_TRIAL": "Trial",
          "NON_RENEWING": "Expiring",
          "PAUSED": "Paused",
          "CANCELLED": "Cancelled",
          "FUTURE": "Future"
        },
        "planTitles": {
          "combined": "Professional Development Plan + Tools Plan",
          "courses": "Professional Development Plan",
          "tools": "Tools Plan"
        },
        "planDescriptions": {
          "combined": "Access to all professional development courses, learning tools and AI assistance.",
          "courses": "Access to all professional development courses.",
          "tools": "Access to all learning tools and AI assistance."
        },
        "offerHeading": "Unlimited use of fobizz for you and your colleagues",
        "offerDescription": "Fobizz offers affordable licenses for professional development and AI tools for schools and educational institutions. Please talk to your school administration about requesting a free trial for your school.",
        "offerButton": "Schedule consultation",
        "offerHint": "Please note: Because of VAT regulations, we currently cannot sell fobizz licenses to <strong>individual users</strong> in countries outside the European Union and Switzerland. We do offer licenses for schools and educational institutions <strong>in all countries</strong>."
      },
      "emailSettings": {
        "title": "Email Settings",
        "subheading": "Email Notifications",
        "labelCourses": "Announcement of new courses, live webinars, discount offers and school promotions (recommended)",
        "frequency": "Newsletter frequency",
        "frequencyOptions": {
          "WEEKLY": "Weekly",
          "NEVER": "Never"
        },
        "save": "Save",
        "successMessage": "Email settings updated.",
        "errorMessage": "The email settings could not be updated."
      },
      "authorizedApplications": {
        "title": "Partner Logins",
        "hint": "These applications are connected to your fobizz account. View all <a href=\"https://app.fobizz.com/partners\" class=\"text-decoration-underline\">partners</a>.",
        "application": "Application",
        "createdAt": "Connected since",
        "revoke": "Revoke",
        "revokeConfirmation": "Are you sure?",
        "noApplications": "No authorized applications."
      },
      "export": {
        "title": "Data Export",
        "description": "Here you can download all your data. The export is provided as a ZIP file and contains all your data in JSON format as well as all uploaded files.",
        "buttonCreate": "Export data",
        "buttonDownload": "Download data export",
        "confirmCreate": "Do you want to start a data export?",
        "processing": "A data export is in progress. It can take several hours to generate the export. You will be notified by email once it is ready.",
        "availableUntil": "The export is available for 3 days and will be deleted after that."
      }
    }
  }
}
